import { OrderFields } from "@modules/order/constants";
import type { TPaymentStatus } from "@modules/order/types";

import {
  buildItem,
  buildUserData,
  CURRENCY_EUR,
  getDeliveryMethodLabel,
  getParcelType,
  isLvEnvironment,
  resetEcommerce,
  TDataLayerUserData,
} from "./dataLayer";
import gtmEcommercePusher from "./gtmEcommercePusher";

export type TOrderItemSource = {
  [OrderFields.PACKAGE_SIZE]?: string | null;
  [OrderFields.DELIVERY_METHOD]?: string | null;
  [OrderFields.DESTINATION_COUNTRY]?: string | null;
  [OrderFields.PRICE]?: number | null;
};

type TItemParams = {
  parcelType: string;
  deliveryMethod: string;
  destinationCountry: string;
  price: string;
};

const getItemParams = (values: TOrderItemSource): TItemParams => ({
  parcelType: getParcelType(values[OrderFields.PACKAGE_SIZE]),
  deliveryMethod: getDeliveryMethodLabel(values[OrderFields.DELIVERY_METHOD]),
  destinationCountry: values[OrderFields.DESTINATION_COUNTRY] ?? "",
  price: (Math.round((values[OrderFields.PRICE] ?? 0) * 100) / 100).toFixed(2),
});

const buildOrderItem = (values: TOrderItemSource) => {
  const { parcelType, deliveryMethod, destinationCountry, price } = getItemParams(values);
  return { price, item: buildItem(parcelType, deliveryMethod, destinationCountry, price) };
};

export const pushViewItem = (user: TDataLayerUserData = {}): void => {
  if (!isLvEnvironment()) {
    return;
  }
  resetEcommerce();
  gtmEcommercePusher({
    event: "view_item",
    ecommerce: { currency: CURRENCY_EUR, value: '0.00', items: [] },
    ...buildUserData(user.email, user.phone),
  });
};

export const pushAddToCart = (values: TOrderItemSource, user: TDataLayerUserData = {}): void => {
  if (!isLvEnvironment()) {
    return;
  }
  const { price, item } = buildOrderItem(values);
  resetEcommerce();
  gtmEcommercePusher({
    event: "add_to_cart",
    ecommerce: {
      currency: CURRENCY_EUR,
      value: price,
      items: [item],
    },
    ...buildUserData(user.email, user.phone),
  });
};

export const pushAddShippingInfo = (values: TOrderItemSource, user: TDataLayerUserData = {}): void => {
  if (!isLvEnvironment()) {
    return;
  }
  const { price, item } = buildOrderItem(values);
  resetEcommerce();
  gtmEcommercePusher({
    event: "add_shipping_info",
    ecommerce: {
      currency: CURRENCY_EUR,
      value: price,
      shipping_tier: item.item_variant,
      items: [item],
    },
    ...buildUserData(user.email, user.phone),
  });
};

export const pushBeginCheckout = (values: TOrderItemSource, user: TDataLayerUserData = {}): void => {
  if (!isLvEnvironment()) {
    return;
  }
  const { price, item } = buildOrderItem(values);
  resetEcommerce();
  gtmEcommercePusher({
    event: "begin_checkout",
    ecommerce: {
      currency: CURRENCY_EUR,
      value: price,
      items: [item],
    },
    ...buildUserData(user.email, user.phone),
  });
};

export const pushPurchase = (paymentStatus: TPaymentStatus, user: TDataLayerUserData = {}): void => {
  if (typeof window === "undefined") {
    return;
  }

  if (!isLvEnvironment()) {
    return;
  }

  const transactionId = paymentStatus.shipmentId;
  if (!transactionId) {
    return;
  }

  const trackedKey = `dpd_purchase_tracked_${transactionId}`;

  try {
    if (localStorage.getItem(trackedKey)) {
      return;
    }
  } catch {
    // localStorage might be unavailable (private mode); fall through and still push once per page.
  }

  const price = (Math.round((paymentStatus.price ?? 0) * 100) / 100).toFixed(2);

  const item = buildItem(
    getParcelType(paymentStatus.packageSize),
    getDeliveryMethodLabel(paymentStatus.deliveryMethod),
    paymentStatus.countryTo ?? "",
    price
  );
  
  resetEcommerce();
  gtmEcommercePusher({
    event: "purchase",
    ecommerce: {
      transaction_id: transactionId,
      currency: CURRENCY_EUR,
      value: price,
      items: [item],
    },
    ...buildUserData(user.email, user.phone),
  });

  try {
    localStorage.setItem(trackedKey, "1");
  } catch {
    // Ignore storage write failures.
  }
};

